(() => {
if (window.SKModProduct) return;
const setText = (node,value) => { if(node && node.textContent !== value) node.textContent=value; };
  class Carousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.skp-carousel-track');
      if (!this.track || !this.track.children.length) return;
      this.cards = [...this.track.children];
      this.originalCards = [...this.cards];
      this.loop = this.dataset.loop === 'true' && this.cards.length > 2;
      this.abort = new AbortController();
      this.pausedByUser = new WeakSet();
      this.centered = this.dataset.stories === 'true';
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.index = 0;
      const options = { signal: this.abort.signal };
      this.querySelector('[data-skp-prev]')?.addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-skp-next]')?.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        this.scheduleCarousel();
        this.scheduleRebalance();
      }, { ...options, passive: true });
      this.track.addEventListener('pointerdown', () => { this.dragging = true; }, { ...options, passive: true });
      const release = () => { this.dragging = false; this.scheduleRebalance(); };
      document.addEventListener('pointerup', release, { ...options, passive: true });
      document.addEventListener('pointercancel', release, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        if (!video) return;
        const actions = card.querySelector('.skp-video-actions');
        if (actions) { actions.hidden = false; video.controls = false; }
        card.querySelector('[data-skp-play]')?.addEventListener('click', () => {
          if (video.paused) { this.pausedByUser.delete(video); this.play(video); }
          else { this.pausedByUser.add(video); video.pause(); }
        }, options);
        card.querySelector('[data-skp-mute]')?.addEventListener('click', () => {
          video.muted = !video.muted; this.videoState(card, video);
        }, options);
        video.addEventListener('play', () => this.videoState(card, video), options);
        video.addEventListener('pause', () => this.videoState(card, video), options);
        video.addEventListener('volumechange', () => this.videoState(card, video), options);
        this.videoState(card, video);
      });
      document.addEventListener('visibilitychange', () => this.syncVideos(), options);
      this.resize = new ResizeObserver(() => this.scheduleCarousel());
      this.resize.observe(this.track);
      if (this.centered) {
        this.visibility = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.4;
          this.syncVideos();
        }, { threshold: [0, 0.4, 0.7] });
        this.visibility.observe(this.track);
      }
      this.refresh();
      if (this.loop || this.centered && this.dataset.initialIndex !== undefined) {
        this.initialFrame = requestAnimationFrame(() => {
          const initial = Math.max(0, Math.min(this.cards.length - 1, Number(this.dataset.initialIndex) || 0));
          if (this.loop) {
            this.track.setAttribute('data-skp-rebasing', '');
            this.rotateToMiddle(this.originalCards[initial]);
          }
          this.scrollToIndex(this.loop ? Math.floor(this.cards.length / 2) : initial, 'instant');
          this.refresh();
          this.restoreSnap();
        });
      }
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initialFrame);
      cancelAnimationFrame(this.snapFrame);
      clearTimeout(this.settleTimer);
      this.abort?.abort(); this.resize?.disconnect(); this.visibility?.disconnect();
      cancelAnimationFrame(this.carouselFrame);
      this.cards?.forEach(card => card.querySelector('video')?.pause());
      this.track?.removeAttribute('data-skp-rebasing');
      if (this.loop) this.originalCards.forEach(card => this.track.append(card));
    }
    scheduleRebalance() {
      if (!this.loop) return;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        if (!this.dragging) { this.refresh(); this.rebalance(); }
      }, 160);
    }
    rotateToMiddle(active) {
      const middle = Math.floor(this.cards.length / 2);
      while (this.cards.indexOf(active) > middle) {
        this.track.append(this.cards.shift());
        this.cards = [...this.track.children];
      }
      while (this.cards.indexOf(active) < middle) {
        this.track.prepend(this.cards.pop());
        this.cards = [...this.track.children];
      }
      this.index = middle;
    }
    restoreSnap() {
      cancelAnimationFrame(this.snapFrame);
      this.snapFrame = requestAnimationFrame(() => this.track.removeAttribute('data-skp-rebasing'));
    }
    rebalance() {
      if (!this.loop || this.dragging || this.index === Math.floor(this.cards.length / 2)) return;
      // Rotate off-screen originals, preserving the active card and its video state.
      // No cloned reviews, duplicate IDs, or extra video downloads are needed.
      const active = this.cards[this.index];
      const before = active.getBoundingClientRect();
      const anchor = rect => this.centered ? rect.left + rect.width / 2 : rect.left;
      this.track.setAttribute('data-skp-rebasing', '');
      this.rotateToMiddle(active);
      const delta = anchor(active.getBoundingClientRect()) - anchor(before);
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'instant' });
      this.refresh();
      this.restoreSnap();
    }
    scheduleCarousel() {
      cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = requestAnimationFrame(() => this.refresh());
    }
    refresh() {
      const rect = this.track.getBoundingClientRect();
      const anchor = this.centered ? rect.left + rect.width / 2 : rect.left;
      let nearest = Infinity;
      this.cards.forEach((card, i) => {
        const item = card.getBoundingClientRect();
        const distance = Math.abs((this.centered ? item.left + item.width / 2 : item.left) - anchor);
        if (distance < nearest) { nearest = distance; this.index = i; }
      });
      const canScroll = this.track.scrollWidth > this.track.clientWidth + 2;
      const nav = this.querySelector('.skp-carousel-nav');
      if (nav) nav.hidden = !canScroll;
      const prev = this.querySelector('[data-skp-prev]'), next = this.querySelector('[data-skp-next]');
      if (prev) prev.disabled = !canScroll || !this.loop && this.track.scrollLeft <= 2;
      if (next) next.disabled = !canScroll || !this.loop && this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      setText(this.querySelector('[data-skp-position]'), `${this.loop ? this.originalCards.indexOf(this.cards[this.index]) + 1 : this.index + 1} / ${this.cards.length}`);
      this.cards.forEach((card, i) => card.classList.toggle('is-current', i === this.index));
      this.syncVideos();
    }
    move(direction) {
      if (this.loop) { this.refresh(); this.rebalance(); }
      const step = Number(this.dataset.step || 1);
      this.scrollToIndex(this.index + direction * step, this.reducedMotion.matches ? 'auto' : 'smooth');
    }
    scrollToIndex(targetIndex, behavior = 'auto') {
      const index = Math.max(0, Math.min(this.cards.length - 1, targetIndex));
      const item = this.cards[index].getBoundingClientRect(), track = this.track.getBoundingClientRect();
      const left = this.track.scrollLeft + item.left - track.left - (this.centered ? (this.track.clientWidth - item.width) / 2 : 0);
      this.track.scrollTo({ left, behavior });
    }
    play(video) {
      const result = video.play();
      result?.catch(() => { /* Autoplay may be blocked; the play button remains available. */ });
    }
    syncVideos() {
      if (!this.centered) return;
      this.cards.forEach((card, i) => {
        const video = card.querySelector('video');
        if (!video) return;
        if (!this.visible || document.hidden || i !== this.index) { if (!video.paused) video.pause(); }
        else if (video.paused && !this.reducedMotion.matches && !this.pausedByUser.has(video)) this.play(video);
      });
    }
    videoState(card, video) {
      const play = card.querySelector('[data-skp-play]'), mute = card.querySelector('[data-skp-mute]');
      if (play) {
        play.setAttribute('aria-label', video.paused ? 'Reproducir vídeo' : 'Pausar vídeo');
        play.setAttribute('aria-pressed', String(!video.paused));
        play.innerHTML = video.paused ? '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m7 3 15 9-15 9z"/></svg>' : '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 3h5v18H5zm9 0h5v18h-5z"/></svg>';
      }
      if (mute) {
        mute.setAttribute('aria-label', video.muted ? 'Activar sonido' : 'Silenciar vídeo');
        mute.setAttribute('aria-pressed', String(!video.muted));
        mute.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 9h4l5-5v16l-5-5H3z"/>' + (video.muted ? '<path d="m16 9 6 6m0-6-6 6"/>' : '<path d="M16 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/>') + '</svg>';
      }
    }
  }

customElements.define('skp-carousel', Carousel);
  function buildPayload(formData, addons, title, group) {
    const id = formData.get('id');
    const quantity = Number(formData.get('quantity') || 1);
    if (!id || !Number.isInteger(quantity) || quantity < 1) throw new Error('Selecciona una variante y una cantidad válida.');
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) properties[match[1]] = value;
    }
    const parent = {id, quantity, properties: {...properties, _skp_group: group}};
    if (formData.get('selling_plan')) parent.selling_plan = formData.get('selling_plan');
    const items = [parent];
    for (const addon of addons) {
      if (!/^\d+$/.test(addon.dataset.id || '') || String(addon.dataset.id) === String(id)) throw new Error('Revisa los complementos seleccionados.');
      items.push({id:addon.dataset.id, quantity, properties:{'Para': title, _skp_group:group}});
    }
    return {items, sections:formData.get('sections') || undefined, sections_url:formData.get('sections_url') || undefined};
  }
  window.SKModProduct = {buildPayload};
  class Rotor extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.select = this.querySelector('[data-skp-rotor-variant]');
      this.toggle = this.querySelector('[data-skp-addon]');
      this.select?.addEventListener('change',()=>this.sync(),{signal:this.abort.signal});
      this.sync();
      const accelerated = document.getElementById(this.dataset.form)?.querySelector('.shopify-payment-button');
      if (accelerated) accelerated.hidden = true;
    }
    sync() {
      const selected = this.select?.selectedOptions[0];
      if (!this.toggle) return;
      this.toggle.disabled = !selected || selected.disabled;
      if (this.toggle.disabled) this.toggle.checked = false;
      this.toggle.dataset.id = selected?.value || '';
      setText(this.querySelector('[data-skp-rotor-price]'),selected?.dataset.price || 'Agotado');
      const image = this.querySelector('.skp-rotor-image');
      const imageUrl = selected?.dataset.image;
      if (image && imageUrl) {
        image.removeAttribute('srcset');
        image.src = imageUrl;
        image.alt = selected.dataset.imageAlt || selected.textContent.trim();
      }
    }
    disconnectedCallback() {this.abort?.abort();}
  }
  class Sticky extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.root = this.closest('product-info'); this.form = document.getElementById(this.dataset.form);
      if (!this.root || !this.form) return;
      this.bar = this.querySelector('.skp-sticky'); this.button = this.querySelector('[data-skp-sticky-add]');
      this.nativeButton = this.form.querySelector('[type=submit]');
      this.button.addEventListener('click',()=>{
        this.submittedFromSticky = true;
        this.nativeButton?.click();
        const error = this.root.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if(error) error.scrollIntoView({block:'center',behavior:'smooth'});
      },{signal:this.abort.signal});
      this.sync = () => {
        this.button.disabled = this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true';
        setText(this.button, this.nativeButton.querySelector('span')?.textContent.trim() || 'Añadir al carrito');
        const price = this.root.querySelector('.price--on-sale .price-item--sale') || this.root.querySelector('.price__regular .price-item--regular');
        setText(this.querySelector('[data-skp-sticky-price]'),price?.textContent.trim() || '');
        const visibleError = this.root.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (this.submittedFromSticky && visibleError) {
          this.submittedFromSticky = false;
          visibleError.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
        }
      };
      this.observer = new MutationObserver(records=>{
        if(records.some(r=>!this.contains(r.target))) this.sync();
      });
      this.observer.observe(this.root,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-disabled','hidden']});
      this.intersection = new IntersectionObserver(entries=>{
        this.bar.hidden = entries[0].isIntersecting || entries[0].boundingClientRect.top > 0;
      });
      this.intersection.observe(this.nativeButton);this.sync();
    }
    disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.intersection?.disconnect();}
  }
  class DetailMedia extends HTMLElement {
    connectedCallback(){
      this.video=this.querySelector('video'); if(!this.video)return;
      this.motion=matchMedia('(prefers-reduced-motion: reduce)');
      this.abort=new AbortController();
      this.sync=()=>{if(this.visible&&!document.hidden&&!this.motion.matches&&!this.manuallyPaused)this.video.play()?.catch(()=>{});else this.video.pause();};
      this.button=this.querySelector('button');
      this.button?.addEventListener('click',()=>{if(this.video.paused){this.manuallyPaused=false;this.video.play()?.catch(()=>{});}else{this.manuallyPaused=true;this.video.pause();}},{signal:this.abort.signal});
      const state=()=>{if(this.button){this.button.textContent=this.video.paused?'▶':'Ⅱ';this.button.setAttribute('aria-label',this.video.paused?'Reproducir detalle':'Pausar detalle');}};
      this.video.addEventListener('play',state,{signal:this.abort.signal});this.video.addEventListener('pause',state,{signal:this.abort.signal});
      if(this.button){this.button.hidden=false;this.video.controls=false;}state();
      document.addEventListener('visibilitychange',this.sync,{signal:this.abort.signal});
      this.motion.addEventListener('change',this.sync,{signal:this.abort.signal});
      this.observer=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.sync();},{threshold:.1});this.observer.observe(this);
    }
    disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.video?.pause();}
  }
  class Stock extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.root = this.closest('product-info');
      this.root?.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) this.hidden = true;
      }, {signal:this.abort.signal});
      if (typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
        if (String(data.sectionId) !== this.dataset.section) return;
        const source = data.html.getElementById(this.id);
        if (!source) return;
        this.innerHTML = source.innerHTML;
        this.hidden = !this.textContent.trim();
      });
    }
    disconnectedCallback() {this.abort?.abort();this.unsubscribe?.();}
  }
  class AmbientVideo extends HTMLElement {
    connectedCallback() {
      this.video=this.querySelector('video'); if(!this.video)return;
      this.abort=new AbortController(); this.motion=matchMedia('(prefers-reduced-motion: reduce)');
      this.video.controls=false; this.video.muted=true; this.video.loop=true;
      this.video.setAttribute('playsinline',''); this.video.setAttribute('tabindex','-1');
      this.video.setAttribute('disablepictureinpicture',''); this.video.setAttribute('disableremoteplayback','');
      this.visible=false;
      this.sync=()=>{
        if(this.visible&&!document.hidden&&!this.motion.matches)this.video.play()?.catch(()=>{});
        else this.video.pause();
      };
      document.addEventListener('visibilitychange',this.sync,{signal:this.abort.signal});
      this.motion.addEventListener('change',this.sync,{signal:this.abort.signal});
      this.observer=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.sync();},{threshold:.1});
      this.sync();this.observer.observe(this);
    }
    disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.video?.pause();}
  }
  customElements.define('skp-ambient-video',AmbientVideo);
  customElements.define('skp-stock', Stock);
  customElements.define('skp-rotor',Rotor);
  customElements.define('skp-sticky',Sticky);
  customElements.define('skp-detail-media',DetailMedia);

})();
